import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  connectJiraWithAtlassian,
  disconnectJira,
  getJiraCredentials,
  saveJiraCredentials,
  testJiraConnection,
} from '../../services/jiraService';
import JiraAssignedTicketsList from './JiraAssignedTicketsList';

const JiraIntegrationPanel = ({ embedded = false, onStatusChange } = {}) => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState(null);
  const [siteUrl, setSiteUrl] = useState('');
  const [email, setEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showTokenForm, setShowTokenForm] = useState(false);

  const loadCredentials = async () => {
    try {
      const data = await getJiraCredentials();
      setCredentials(data);
      setEmail(data.email || '');
      if (data.organizationSiteConfigured) {
        setSiteUrl(data.organizationSiteUrl || '');
        setShowTokenForm(true);
      } else {
        setSiteUrl(data.siteUrl || '');
        setShowTokenForm(false);
      }
    } catch {
      setCredentials(null);
    }
  };

  useEffect(() => {
    loadCredentials();
  }, []);

  useEffect(() => {
    onStatusChange?.(Boolean(credentials?.isConnected));
  }, [credentials, onStatusChange]);

  const handleAtlassianLogin = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await connectJiraWithAtlassian();
      await loadCredentials();
      setSuccess('Jira connected. Assigned tickets are available in Workflows.');
    } catch (err) {
      setError(err.message);
      await loadCredentials();
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await saveJiraCredentials({
        siteUrl: credentials?.organizationSiteConfigured ? '' : siteUrl,
        email,
        apiToken,
      });
      setCredentials(data);
      setApiToken('');
      setShowTokenForm(false);
      setSuccess('Jira connected. Assigned tickets are available in Workflows.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setSuccess('');
    try {
      await testJiraConnection();
      setSuccess('Jira connection is active.');
      await loadCredentials();
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Jira from WorkLoop?')) return;
    setLoading(true);
    setError('');
    try {
      await disconnectJira();
      setCredentials(null);
      setApiToken('');
      setShowTokenForm(false);
      setSuccess('Jira disconnected.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const connected = Boolean(credentials?.isConnected);
  const oauthAvailable = credentials?.oauthLoginAvailable !== false;
  const orgSiteConfigured = Boolean(credentials?.organizationSiteConfigured);
  const orgSiteUrl = credentials?.organizationSiteUrl || '';

  return (
    <div className={embedded ? '' : 'max-w-4xl mx-auto p-6'}>
      {!embedded ? (
        <>
      <button
        type="button"
        onClick={() => navigate('/integrations')}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Plugins
      </button>

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Jira Integration</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Sign in with Atlassian. Company SSO (Okta, Azure AD, Google Workspace) works on that login screen.
      </p>
        </>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
          {success}
        </div>
      ) : null}

      {connected ? (
        <div className="mb-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Integrated</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {credentials.displayName || credentials.email || 'Atlassian account'}
            {credentials.siteUrl ? ` · ${credentials.siteUrl}` : ''}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {credentials.authType === 'oauth' ? 'Signed in with Atlassian' : 'Connected with API token'}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {credentials.siteUrl ? (
              <a
                href={credentials.siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                Open Jira
              </a>
            ) : null}
            <button
              type="button"
              onClick={handleTest}
              disabled={testing}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button
              type="button"
              onClick={handleAtlassianLogin}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            >
              Reconnect
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg text-rose-600 dark:text-rose-400"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {orgSiteConfigured
              ? 'Your organization Jira site is already set. Enter your Jira username/email and API token to load your tickets. You can also sign in with Atlassian (SSO).'
              : 'Click below to open Atlassian. Sign in with your usual Jira account, including SSO if your company uses it.'}
          </p>
          <button
            type="button"
            onClick={handleAtlassianLogin}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold rounded-lg bg-[#1868DB] text-white hover:bg-[#1558b0] disabled:opacity-50"
          >
            {loading ? 'Waiting for Atlassian…' : 'Continue with Atlassian'}
          </button>
          {!oauthAvailable ? (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Atlassian login is not configured on this server yet. An admin must add a Jira OAuth app, or use an API token below.
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => setShowTokenForm((open) => !open)}
            className="text-xs font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
          >
            {showTokenForm ? 'Hide API token option' : 'Use API token instead'}
          </button>

          {showTokenForm ? (
            <form onSubmit={handleConnect} className="space-y-3 pt-1">
              {orgSiteConfigured ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Using organization Jira: <span className="font-semibold text-gray-700 dark:text-gray-200">{orgSiteUrl}</span>
                </p>
              ) : (
                <input
                  type="url"
                  required
                  value={siteUrl}
                  onChange={(e) => setSiteUrl(e.target.value)}
                  placeholder="https://your-company.atlassian.net"
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                />
              )}
              <input
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Jira username or email"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
              <input
                type="password"
                required
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder="API token"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Connecting…' : 'Connect with API token'}
              </button>
            </form>
          ) : null}
        </div>
      )}

      {connected ? (
        <div className="mt-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Assigned to you</h2>
          </div>
          <JiraAssignedTicketsList />
        </div>
      ) : null}
    </div>
  );
};

export default JiraIntegrationPanel;
